import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { profileService } from '@/services/profile.service'
import { QUERY_KEYS } from '@/constants'
import { useAuth } from '@/features/auth/hooks/useAuth'
import type { ProfileEditFormValues } from '@/types'

// ── Current authed user's profile ────────────────────────────

export function useCurrentProfile() {
  const { user, isAuthenticated } = useAuth()

  const query = useQuery({
    queryKey: ['profile', 'current', user?.id],
    queryFn:  () => profileService.getById(user!.id),
    enabled:  isAuthenticated && !!user?.id,
    staleTime: 1000 * 60 * 5,
  })

  return { profile: query.data ?? null, ...query }
}

// ── Any profile by username ───────────────────────────────────

export function useProfile(username: string) {
  return useQuery({
    queryKey: QUERY_KEYS.PROFILE(username),
    queryFn:  () => profileService.getByUsername(username),
    enabled:  !!username,
    staleTime: 1000 * 60 * 5,
  })
}

// ── Update profile ────────────────────────────────────────────

export function useUpdateProfile() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  return useMutation({
    mutationFn: (values: ProfileEditFormValues) => {
      if (!user?.id) throw new Error('Not authenticated')
      return profileService.update(user.id, values)
    },
    onSuccess: (updated) => {
      // Invalidate both the current-user cache and the by-username cache
      queryClient.invalidateQueries({ queryKey: ['profile', 'current', user?.id] })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PROFILE(updated.username) })
    },
  })
}

// ── Update avatar ─────────────────────────────────────────────

export function useUpdateAvatar() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  return useMutation({
    mutationFn: (file: File) => {
      if (!user?.id) throw new Error('Not authenticated')
      return profileService.updateAvatar(user.id, file)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile', 'current', user?.id] })
    },
  })
}
